import { parseHHmmToMinutes } from './werkbon-hours'

/**
 * FO-135 (batch-27) — gedeelde nachtdienst-definitie voor de planning.
 *
 * Eén bron van waarheid: het planbord-display (paarse nachtdienst-badge), de
 * edit-validaties (los + bulk) en de urenberekening leiden "loopt deze dienst
 * over middernacht?" allemaal uit dezelfde regel af, zodat weergave en
 * bewerking nooit meer uit de pas lopen.
 */

/**
 * Een dienst is een nachtdienst wanneer de eindtijd numeriek vóór de begintijd
 * ligt: de dienst eindigt dan de volgende kalenderdag (bijv. 22:00 tot 06:00).
 *
 * Gelijke tijden (`eind == start`) zijn GEEN nachtdienst — dat blijft elders
 * bewust ongeldig (geen 0- of impliciet 24-urige dienst), consistent met de
 * bestaande consignatie- en werkbon-regel. Ongeldige HH:mm-invoer geeft
 * `false`; de aanroeper valideert het formaat apart.
 */
export function isNachtdienst(startHHmm: string, endHHmm: string): boolean {
  const start = parseHHmmToMinutes(startHHmm)
  const eind = parseHHmmToMinutes(endHHmm)
  if (start == null || eind == null) return false
  return eind < start
}

/**
 * Netto dienstduur in minuten tussen twee HH:mm-tijden, ná aftrek van de pauze.
 * Een nachtdienst (eind < start) telt 24 uur (1440 min) bij de eindtijd op zodat
 * de duur over de middernacht-overgang klopt: 22:00 tot 06:00 met 0 pauze = 480
 * min (8 uur), met 30 min pauze = 450 min.
 *
 * Spiegelt exact de +24u-regel van `computeDayEntryHours` (werkbon-uren) zodat
 * het planbord dezelfde duur berekent als de opgeslagen dag-entry. Retourneert
 * `null` bij ongeldige HH:mm-invoer. Bij gelijke tijden (`eind == start`) is de
 * ruwe duur 0 minus pauze; de aanroeper weert dat geval apart af als ongeldig.
 * De uitkomst kan negatief zijn wanneer de pauze langer is dan de werktijd — de
 * aanroeper bepaalt of dat een fout is.
 */
export function berekenDienstMinuten(
  startHHmm: string,
  endHHmm: string,
  pauzeMin: number,
): number | null {
  const startMin = parseHHmmToMinutes(startHHmm)
  const eindMinRaw = parseHHmmToMinutes(endHHmm)
  if (startMin == null || eindMinRaw == null) return null
  const eindMin = eindMinRaw < startMin ? eindMinRaw + 1440 : eindMinRaw
  return eindMin - startMin - pauzeMin
}
